import React, { useEffect, useState } from "react";
import { Loader2, Trash2, Newspaper, Globe2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { adminApi } from "@/lib/adminApi";
import AdminPageHeader from "@/components/admin/AdminPageHeader";

function fmtDate(iso) {
    try {
        return new Intl.DateTimeFormat("de-AT", { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
    } catch {
        return iso;
    }
}

export default function AdminNewsletter() {
    const [data, setData] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [copied, setCopied] = useState(false);
    const [deleting, setDeleting] = useState(null);

    const load = () => {
        setLoading(true);
        adminApi
            .newsletter({ limit: 500 })
            .then(setData)
            .catch(() => toast.error("Failed to load"))
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    const onDelete = async (id) => {
        setDeleting(id);
        try {
            await adminApi.deleteNewsletter(id);
            toast.success("Removed");
            setData((d) => ({ ...d, items: d.items.filter((x) => x.id !== id), total: d.total - 1 }));
        } catch {
            toast.error("Failed to remove");
        } finally {
            setDeleting(null);
        }
    };

    const onExport = async () => {
        setExporting(true);
        try {
            await adminApi.exportCsv("newsletter");
        } catch {
            toast.error("Export failed");
        } finally {
            setExporting(false);
        }
    };

    const copyAll = () => {
        const text = data.items.map((i) => i.email).join(", ");
        navigator.clipboard.writeText(text);
        setCopied(true);
        toast.success("Email list copied");
        setTimeout(() => setCopied(false), 1500);
    };

    return (
        <div data-testid="admin-newsletter">
            <AdminPageHeader
                eyebrow="Admin · Audience"
                title="Newsletter subscribers"
                count={data.total}
                onRefresh={load}
                onExport={onExport}
                exporting={exporting}
            >
                <Button
                    data-testid="copy-emails"
                    onClick={copyAll}
                    disabled={data.items.length === 0}
                    variant="outline"
                    size="sm"
                    className="mt-3 rounded-full border-caireen-ink/15"
                >
                    {copied ? <Check className="w-3.5 h-3.5 mr-1.5" /> : <Copy className="w-3.5 h-3.5 mr-1.5" />}
                    Copy all emails
                </Button>
            </AdminPageHeader>

            <div className="bg-white rounded-3xl ring-1 ring-caireen-ink/10 overflow-hidden">
                {loading ? (
                    <div className="flex items-center justify-center py-24 text-caireen-ink/40">
                        <Loader2 className="w-5 h-5 animate-spin" />
                    </div>
                ) : data.items.length === 0 ? (
                    <div className="text-center py-20 font-sans text-sm text-caireen-ink/55">
                        <Newspaper className="w-8 h-8 mx-auto mb-3 text-caireen-ink/30" />
                        No subscribers yet.
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-caireen-cream/60">
                                <TableHead className="font-sans text-xs uppercase tracking-widest text-caireen-ink/55">Subscribed</TableHead>
                                <TableHead className="font-sans text-xs uppercase tracking-widest text-caireen-ink/55">Email</TableHead>
                                <TableHead className="font-sans text-xs uppercase tracking-widest text-caireen-ink/55">Language</TableHead>
                                <TableHead className="font-sans text-xs uppercase tracking-widest text-caireen-ink/55 text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {data.items.map((s) => (
                                <TableRow key={s.id} className="hover:bg-caireen-cream/40">
                                    <TableCell className="font-sans text-sm text-caireen-ink/70 whitespace-nowrap">{fmtDate(s.created_at)}</TableCell>
                                    <TableCell className="font-sans text-sm font-semibold">{s.email}</TableCell>
                                    <TableCell>
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-caireen-ink/5 font-sans text-[10px] uppercase tracking-widest">
                                            <Globe2 className="w-3 h-3" />
                                            {s.language?.toUpperCase() || "—"}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Button
                                            data-testid={`newsletter-delete-${s.id}`}
                                            variant="ghost"
                                            size="sm"
                                            disabled={deleting === s.id}
                                            onClick={() => onDelete(s.id)}
                                            className="text-caireen-red hover:bg-caireen-red/10 rounded-full"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </div>
        </div>
    );
}
